import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"; // qc used in NewOrgDialog
import { toast } from "sonner";
import { Pencil, Settings } from "lucide-react";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import SearchStatusBar from "@/components/common/SearchStatusBar";
import EditOrganizationDialog, {
  type Organization,
} from "@/features/organizations/EditOrganizationDialog";

const STATUS_OPTIONS = [
  { value: "ALL",      label: "全部状态" },
  { value: "ACTIVE",   label: "ACTIVE" },
  { value: "DISABLED", label: "DISABLED" },
];

export default function OrganizationsPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [editing, setEditing] = useState<Organization | null>(null);
  const [showNew, setShowNew] = useState(false);

  const list = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/organizations");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Organization[];
    },
  });

  const filtered = useMemo(() => {
    const rows = list.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((o) => {
      if (status !== "ALL" && o.status !== status) return false;
      if (needle && !(o.name + " " + o.emailDomain).toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [list.data, q, status]);

  return (
    <div className="space-y-6">
      {editing && (
        <EditOrganizationDialog org={editing} onClose={() => setEditing(null)} />
      )}
      {showNew && <NewOrgDialog onClose={() => setShowNew(false)} />}

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">公司管理</h1>
          <p className="text-sm text-muted-foreground mt-1">
            共 {list.data?.length ?? 0} 家公司
            {(() => {
              const disabled = (list.data ?? []).filter((o) => o.status === "DISABLED").length;
              return disabled > 0 ? <> · 已禁用 <strong className="text-foreground">{disabled}</strong></> : null;
            })()}
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:opacity-90"
        >
          + 新建公司
        </button>
      </div>

      <SearchStatusBar
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
        placeholder="搜索名称或邮箱域名"
      />

      <section className="rounded-lg border border-border bg-background overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-medium">名称</th>
              <th className="text-left px-4 py-2 font-medium">邮箱域名</th>
              <th className="text-left px-4 py-2 font-medium">状态</th>
              <th className="text-left px-4 py-2 font-medium">创建时间</th>
              <th className="text-right px-4 py-2 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">加载中…</td></tr>
            )}
            {!list.isLoading && (list.data?.length ?? 0) === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">暂无公司</td></tr>
            )}
            {!list.isLoading && (list.data?.length ?? 0) > 0 && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">没有符合筛选条件的公司</td></tr>
            )}
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-border">
                <td className="px-4 py-2 font-medium">
                  <Link to={`/organizations/${o.id}`} className="text-primary hover:underline">
                    {o.name}
                  </Link>
                </td>
                <td className="px-4 py-2 font-mono text-xs">{o.emailDomain}</td>
                <td className="px-4 py-2">
                  <span className={
                    "text-xs px-2 py-0.5 rounded " +
                    (o.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700")
                  }>
                    {o.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">
                  {new Date(o.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right space-x-3 text-xs">
                  <button
                    onClick={() => setEditing(o)}
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> 编辑
                  </button>
                  <Link
                    to={`/organizations/${o.id}`}
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <Settings className="w-3 h-3" /> 管理
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function NewOrgDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [emailDomain, setEmailDomain] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/organizations", {
        body: { name, emailDomain },
      });
      if (error || !response.ok) throw error || new Error("创建失败");
      return data as unknown as Organization;
    },
    onSuccess: (org) => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(`公司 "${org.name}" 创建成功`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">新建公司</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          className="p-5 space-y-4 text-sm"
        >
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">公司名称</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">邮箱域名</span>
            <input
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              required
              placeholder="例：acme.com"
              pattern="^[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending ? "创建中…" : "创建"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
